import React, {useMemo, Suspense, lazy} from "react";
import * as styles from "./styles/HomeHeader.css";

const InitRenderSwitch = require("../core/view/InitRenderSwitch");

// Lazy load card components to reduce initial bundle size
const HomeStudentCard = lazy(() => import("./HomeStudentCard"));
const HomeUserCard = lazy(() => import("./HomeUserCard"));
const AddStudentCard = lazy(() => import("./AddStudentCard"));
const DeactivatedStudentCard = lazy(() => import("./DeactivatedStudentCard"));

const OpsUser = require("../core/vault/OpsUser");

interface HomeHeaderProps {
	user: any;
}

const HomeHeader = ({user}: HomeHeaderProps) => {
	// Memoize expensive filtering operation
	const activeStudents = useMemo(() => {
		const students = user?.model?.students || [];
		return students.filter((s: any) => s?.isActive);
	}, [user?.model?.students]);

	// these are students enrolled in classes
	// they won't show up in activeStudents because they get
	// access via class_relations, not via subscriptions
	const enrolledStudents = useMemo(() => {
		const students = user?.model?.students || [];
		return students.filter((s: any) => s?.inActiveClasses);
	}, [user?.model?.students]);

	const inactiveStudents = useMemo(() => {
		const students = user?.model?.students || [];
		return students.filter((s: any) => !s?.isActive && !s?.inActiveClasses);
	}, [user?.model?.students]);

	// Memoize user type checks to avoid recalculation
	const userType = useMemo(() => {
		// Safety check - ensure user object exists before calling OpsUser methods
		if (!user || !user.model) {
			return {
				isParent: false,
				isStudent: false,
				isStudentDeactivated: false,
				hasDashboardAccess: false,
			};
		}

		return {
			isParent: Boolean(OpsUser.hasRole(user, "parent")),
			isStudent: Boolean(OpsUser.hasRole(user, "student")),
			isStudentDeactivated: Boolean(
				OpsUser.hasRole(user, "student deactivated")
			),
			hasDashboardAccess: Boolean(OpsUser.hasDashboard(user)),
		};
	}, [user]);

	const {
		isParent,
		isStudent,
		isStudentDeactivated,
		hasDashboardAccess,
	} = userType;

	// Determine header text based on user type
	// Don't count deactivated students as active users
	const isParentOrStudent = isParent || (isStudent && !isStudentDeactivated);
	const headerText = isParentOrStudent ? "Welcome back!" : "Welcome!";

	// Show loading skeleton if user data isn't ready yet
	const isLoading = !user || !user.authenticated;

	// Show loading skeleton while user data is loading
	if (isLoading) {
		return (
			<section className={styles.section}>
				<div className={styles.container}>
					<div className={styles.content}>
						<h1>Welcome!</h1>
						<div className={styles.studentGrid}>
							<div className={styles.skeletonCard}>
								<div className={styles.skeletonAvatar} />
								<div className={styles.skeletonText}>
									<div className={styles.skeletonLine} />
									<div className={styles.skeletonLineShort} />
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>
		);
	}

	// Skeleton fallback for lazy-loaded card components
	const CardSkeleton = () => (
		<div className={styles.skeletonCard}>
			<div className={styles.skeletonAvatar} />
			<div className={styles.skeletonText}>
				<div className={styles.skeletonLine} />
				<div className={styles.skeletonLineShort} />
			</div>
		</div>
	);

	const studentGridPlaceholder = (
		<div className={styles.studentGrid}>
			<CardSkeleton />
		</div>
	);

	const studentGridContent = (
		<div className={styles.studentGrid}>
			<Suspense fallback={<CardSkeleton />}>
				{isStudentDeactivated ? (
					<DeactivatedStudentCard user={user} isViewedByParent={false} />
				) : (
					<>
						{isParentOrStudent && <HomeUserCard user={user} />}
						{isParent && (
							<>
								{activeStudents.map((student: any) => (
									<HomeStudentCard
										key={student.id}
										student={student}
										method="from-loggedin-home"
									/>
								))}
							</>
						)}
						{isParent && (
							<>
								{enrolledStudents.map((student: any) => (
									<HomeStudentCard
										key={student.id}
										student={student}
										method="from-loggedin-home"
									/>
								))}
							</>
						)}
						{isParent && (
							<>
								{inactiveStudents.map((student: any) => (
									<DeactivatedStudentCard
										key={student.id}
										user={student}
										isViewedByParent={true}
									/>
								))}
							</>
						)}
						{isParent && <AddStudentCard />}
					</>
				)}
			</Suspense>
		</div>
	);

	return (
		<section className={styles.section}>
			<div className={styles.container}>
				<div className={styles.content}>
					<h1>{headerText}</h1>
					{isParent && !isStudentDeactivated && (
						<p className={styles.textBlue}>Who's using Beast Academy today?</p>
					)}
					<InitRenderSwitch forFirstRender={studentGridPlaceholder}>
						{studentGridContent}
					</InitRenderSwitch>
					{hasDashboardAccess && (
						<div className={styles.buttons}>
							<a
								href="/dashboard/reports/system/schools"
								className={`${styles.button} ${styles.buttonPrimary}`}
							>
								Go to Reports
							</a>
							<a
								href="/dashboard/accounts/system/schools"
								className={`${styles.button} ${styles.buttonSecondary}`}
							>
								Go to Accounts
							</a>
						</div>
					)}
					{isParent && (
						<div className={styles.buttons}>
							<a
								href="/parent/students"
								className={`${styles.button} ${styles.buttonSecondary}`}
							>
								View Reports
							</a>
							<a
								href="/account/subscriptions"
								className={`${styles.button} ${styles.buttonSecondary}`}
							>
								Manage Accounts
							</a>
						</div>
					)}
				</div>
			</div>
		</section>
	);
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(HomeHeader);
